import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { BrandLockup } from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "@/i18n/LanguageContext";

const Header = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV_LINKS = [
    { label: t.nav.home, href: "#home" },
    { label: t.nav.products, href: "#products" },
    { label: t.nav.logistics, href: "#logistics" },
    { label: t.nav.compliance, href: "#compliance" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A192F]/95 backdrop-blur-md border-b border-[#D4AF37]/25"
          : "bg-[#0A192F]/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNav("#home"); }}
          data-testid="header-logo-link"
          className="shrink-0"
        >
          <BrandLockup compact />
        </a>

        <nav className="hidden xl:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => { e.preventDefault(); handleNav(l.href); }}
              data-testid={`nav-link-${l.href.replace('#','')}`}
              className="font-sans text-[12px] tracked text-steel hover:text-[#D4AF37] transition-colors duration-300 uppercase whitespace-nowrap"
              style={{ letterSpacing: "0.18em" }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden lg:block">
            <LanguageSwitcher variant="desktop" />
          </div>

          <button
            data-testid="header-initiate-trade-btn"
            onClick={() => handleNav("#contact")}
            className="hidden md:inline-flex btn-crimson px-5 py-3 text-[12px] font-sans font-semibold uppercase tracked border border-[#7A1C1C]"
            style={{ letterSpacing: "0.2em", minHeight: 44 }}
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors"
            style={{ width: 44, height: 44 }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu-drawer"
        className={`xl:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        } bg-[#020C1B] border-t border-[#D4AF37]/20`}
      >
        <nav className="px-5 py-6 flex flex-col">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => { e.preventDefault(); handleNav(l.href); }}
              data-testid={`mobile-nav-link-${l.href.replace('#','')}`}
              className="font-sans text-sm text-bone tracked uppercase py-4 border-b border-[#D4AF37]/10"
              style={{ letterSpacing: "0.2em", minHeight: 44 }}
            >
              {l.label}
            </a>
          ))}

          <div className="mt-6 flex items-center justify-between">
            <span className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.28em" }}>
              {t.lang.switch_aria}
            </span>
            <LanguageSwitcher variant="mobile" />
          </div>

          <button
            data-testid="mobile-initiate-trade-btn"
            onClick={() => handleNav("#contact")}
            className="mt-6 btn-crimson px-5 py-4 text-[12px] font-sans font-semibold uppercase tracked"
            style={{ letterSpacing: "0.2em", minHeight: 48 }}
          >
            {t.nav.cta}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
